@if (summary$ | async; as s) {
  <div class="p-4 shift-summary-output">
    <div class="flex align-items-center mb-3">
      <p-button icon="pi pi-arrow-left" class="p-button-text mr-2" (onClick)="back()"></p-button>
      <h2 class="m-0">Shift Summary</h2>
    </div>

    @if (s.outputSnapshot?.outputTitle) {
      <div class="output-title mb-4">
        <h3>{{ s.outputSnapshot!.outputTitle }}</h3>
      </div>
    }

    <p-panel header="Job Identification" class="mb-3">
      <div class="grid">
        <div class="col-12 md:col-4">
          <span class="field-label">Customer</span>
          <span class="field-value">{{ formatNA(s.outputSnapshot?.customer) }}</span>
        </div>
        <div class="col-12 md:col-4">
          <span class="field-label">ESS Job Number</span>
          <span class="field-value">{{ formatNA(s.outputSnapshot?.essJobNumber) }}</span>
        </div>
        <div class="col-12 md:col-4">
          <span class="field-label">Well Name</span>
          <span class="field-value">{{ formatNA(s.outputSnapshot?.wellName) }}</span>
        </div>
        <div class="col-12 md:col-4">
          <span class="field-label">District</span>
          <span class="field-value">{{ formatNA(s.outputSnapshot?.district) }}</span>
        </div>
        <div class="col-12 md:col-4">
          <span class="field-label">Date &amp; Time of Report</span>
          <span class="field-value">{{ s.outputSnapshot?.reportDateTimeUtc | date:'medium' }}</span>
        </div>
      </div>
    </p-panel>

    <p-panel header="Shift Details" class="mb-3">
      <div class="grid">
        <div class="col-12 md:col-3">
          <span class="field-label">Shift Start</span>
          <span class="field-value">{{ s.shiftStartUtc | date:'medium' }}</span>
        </div>
        <div class="col-12 md:col-3">
          <span class="field-label">Shift End</span>
          <span class="field-value">{{ s.shiftEndUtc | date:'medium' }}</span>
        </div>
        <div class="col-12 md:col-3">
          <span class="field-label">Day / Night</span>
          <span class="field-value">{{ s.dayNight }}</span>
        </div>
        <div class="col-12 md:col-3">
          <span class="field-label">Total Hours Worked</span>
          <span class="field-value">{{ formatNumber(s.totalHoursWorked) }}</span>
        </div>
        <div class="col-12 md:col-6">
          <span class="field-label">Crew Members</span>
          <span class="field-value">{{ formatNA(s.crewMembers) }}</span>
        </div>
        <div class="col-12 md:col-3">
          <span class="field-label">Current Status</span>
          <span class="field-value">{{ formatNA(s.currentStatus) }}</span>
        </div>
        <div class="col-12 md:col-3">
          <span class="field-label">Created By</span>
          <span class="field-value">{{ formatNA(s.createdBy) }}</span>
        </div>
      </div>
    </p-panel>

    <p-panel header="Safety" class="mb-3">
      <div class="grid">
        <div class="col-12">
          <span class="field-label">Safety Topic(s) Discussed</span>
          <span class="field-value">{{ formatNA(s.safetyTopicsDiscussed) }}</span>
        </div>
        <div class="col-12 md:col-3">
          <span class="field-label">Any Safety Incidents?</span>
          <span class="field-value">{{ formatYesNo(s.anySafetyIncidents) }}</span>
        </div>
        @if (s.anySafetyIncidents === true) {
          <div class="col-12 md:col-9">
            <span class="field-label">Safety Incident Details</span>
            <span class="field-value">{{ formatNA(s.safetyIncidentDetails) }}</span>
          </div>
          @if (s.safetyIncidents?.length) {
            <div class="col-12 md:col-9">
              <span class="field-label">Incidents</span>
              <span class="field-value">{{ s.safetyIncidents!.join(', ') }}</span>
            </div>
          }
        }
      </div>
    </p-panel>

    <p-panel header="Narratives" class="mb-3">
      <div class="grid">
        <div class="col-12 field-block">
          <span class="field-label">Job Status</span>
          <p class="field-value pre-wrap">{{ formatNA(s.jobStatus) }}</p>
        </div>
        <div class="col-12 field-block">
          <span class="field-label">Last Discussion with Customer</span>
          <p class="field-value pre-wrap">{{ formatNA(s.customerFeedback) }}</p>
        </div>
        <div class="col-12 field-block">
          <span class="field-label">Mechanical Needs</span>
          <p class="field-value pre-wrap">{{ formatNA(s.mechanicalNeeds) }}</p>
        </div>
        <div class="col-12 field-block">
          <span class="field-label">Next Planned Work</span>
          <p class="field-value pre-wrap">{{ formatNA(s.nextPlannedWork) }}</p>
        </div>
      </div>
    </p-panel>

    <p-panel header="Operational Checks" class="mb-3">
      <div class="grid">
        <div class="col-12 md:col-4">
          <span class="field-label">Data Components Working?</span>
          <span class="field-value">{{ formatYesNo(s.dataComponentsWorking) }}</span>
        </div>
        @if (s.dataComponentsWorking === false) {
          <div class="col-12 md:col-8">
            <span class="field-label">Data Components Context</span>
            <span class="field-value">{{ formatNA(s.dataComponentsContext) }}</span>
          </div>
        }
        <div class="col-12 md:col-4">
          <span class="field-label">{{ fluidTypeLabel(s.summaryProfile) }}</span>
          <span class="field-value">{{ formatNA(s.fluidType) }}</span>
        </div>
        @if (supportsCurrentFluidSystem(s.summaryProfile)) {
          <div class="col-12 md:col-4">
            <span class="field-label">Current Fluid System</span>
            <span class="field-value">{{ formatNA(s.currentFluidSystem) }}</span>
          </div>
        }
        @if (supportsFoamMetrics(s.summaryProfile)) {
          <div class="col-12 md:col-4">
            <span class="field-label">Foam Stability Time (min)</span>
            <span class="field-value">{{ formatNA(s.foamStabilityMinutes) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Sand Suspension Time (min)</span>
            <span class="field-value">{{ formatNA(s.sandSuspensionMinutes) }}</span>
          </div>
        }
      </div>
    </p-panel>

    <p-panel header="Consumables" class="mb-3">
      <div class="grid">
        <div class="col-12 md:col-3">
          <span class="field-label">ISCAV (gallons)</span>
          <span class="field-value">{{ formatNA(s.iscavGallonsConsumed) }}</span>
        </div>
        <div class="col-12 md:col-3">
          <span class="field-label">Procide (gallons)</span>
          <span class="field-value">{{ formatNA(s.procideGallonsConsumed) }}</span>
        </div>
        <div class="col-12 md:col-6">
          <span class="field-label">Fuel Provided by Customer?</span>
          <span class="field-value">{{ formatYesNo(s.fuelProvidedByCustomer) }}</span>
        </div>
      </div>
    </p-panel>

    @if (s.outputSnapshot) {
      <p-panel header="Vendors on Location" class="mb-3">
        <div class="grid">
          <div class="col-12 md:col-3">
            <span class="field-label">CT/WOR Provider</span>
            <span class="field-value">{{ formatNA(s.outputSnapshot.ctWorProvider) }}</span>
          </div>
          <div class="col-12 md:col-3">
            <span class="field-label">DH Tool Provider</span>
            <span class="field-value">{{ formatNA(s.outputSnapshot.dhToolProvider) }}</span>
          </div>
          <div class="col-12 md:col-3">
            <span class="field-label">Tubing Unit Description</span>
            <span class="field-value">{{ formatNA(s.outputSnapshot.tubingUnitDescription) }}</span>
          </div>
          <div class="col-12 md:col-3">
            <span class="field-label">Flowback Provider</span>
            <span class="field-value">{{ formatNA(s.outputSnapshot.flowbackProvider) }}</span>
          </div>
        </div>
      </p-panel>

      <p-panel header="Job Progression" class="mb-3">
        <div class="grid">
          <div class="col-12 md:col-4">
            <span class="field-label">PBTD</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.pbtd) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Current Depth</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.currentDepth) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Deepest Depth Recorded</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.deepestDepthRecorded) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Footage Moved During Shift</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.footageMovedDuringShift) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">ROP Over Shift</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.ropOverShift) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Footage Remaining</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.footageRemaining) }}</span>
          </div>
        </div>
      </p-panel>

      <p-panel header="Live Data Status" class="mb-3">
        <div class="grid">
          <div class="col-12 md:col-4">
            <span class="field-label">Live Data Pushed Over Shift</span>
            <span class="field-value">{{ formatPercentage(s.outputSnapshot.liveDataPushedPercent) }}</span>
          </div>
        </div>

        @if (s.outputSnapshot.devicePushStatuses?.length) {
          <p-table
            [value]="s.outputSnapshot.devicePushStatuses!"
            [tableStyle]="{'min-width': '30rem'}">
            <ng-template pTemplate="header">
              <tr>
                <th>Device</th>
                <th>Last Data Pushed</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ formatNA(row.deviceName) }}</td>
                <td>{{ row.lastDataPushedAtUtc ? (row.lastDataPushedAtUtc | date:'medium') : 'N/A' }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      </p-panel>

      <p-panel header="Plugs" class="mb-3">
        <div class="grid">
          <div class="col-12 md:col-4">
            <span class="field-label">Plugs in Well</span>
            <span class="field-value">{{ formatNA(s.outputSnapshot.plugsInWell) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Total Plugs Milled</span>
            <span class="field-value">{{ formatNA(s.outputSnapshot.totalPlugsMilled) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Plugs Remaining</span>
            <span class="field-value">{{ formatNA(s.outputSnapshot.plugsRemaining) }}</span>
          </div>
        </div>
      </p-panel>
    }

    @if (s.outputSnapshot?.liveDataReport?.length) {
      <p-panel header="Live Data Report" class="mb-3">
        <p-table [value]="s.outputSnapshot!.liveDataReport!" [tableStyle]="{'min-width': '30rem'}">
          <ng-template pTemplate="header">
            <tr>
              <th>Metric</th>
              <th>Avg Over Shift</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.metricLabel }}</td>
              <td>{{ formatNumber(row.avgOverShift) }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="2">No data available</td></tr>
          </ng-template>
        </p-table>
      </p-panel>
    }

    @if (s.outputSnapshot?.workbookDataReport?.length) {
      <p-panel header="Workbook Data Report" class="mb-3">
        <p-table [value]="s.outputSnapshot!.workbookDataReport!" [tableStyle]="{'min-width': '40rem'}">
          <ng-template pTemplate="header">
            <tr>
              <th>Metric</th>
              <th># of Entries</th>
              <th>Current</th>
              <th>Avg Over Shift</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.metricLabel }}</td>
              <td>{{ formatNA(row.numberOfEntries) }}</td>
              <td>{{ formatNumber(row.current) }}</td>
              <td>{{ formatNumber(row.avgOverShift) }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="4">No data available</td></tr>
          </ng-template>
        </p-table>
      </p-panel>
    }

    @if (s.outputSnapshot) {
      <p-panel header="LCM Report" class="mb-3">
        <div class="grid">
          <div class="col-12 md:col-4">
            <span class="field-label">Part C Test Status</span>
            <span class="field-value">{{ formatPartCTestStatus(s.partCTestStatus) }}</span>
          </div>
          @if (showPartCDetails(s.partCTestStatus)) {
            <div class="col-12 md:col-4">
              <span class="field-label">Grams to Pass</span>
              <span class="field-value">{{ formatNA(s.gramsToPass) }}</span>
            </div>
            <div class="col-12 md:col-4">
              <span class="field-label">Water Leak Off</span>
              <span class="field-value">{{ formatNA(s.waterLeakOff) }}</span>
            </div>
          }
          <div class="col-12 md:col-4">
            <span class="field-label">BBLS of LCM Pumped</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.lcmReport?.bblsOfLcmPumped) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">LBS of Part C</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.lcmReport?.lbsOfPartC) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Avg LBS Part C / BBL</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.lcmReport?.avgLbsPartCPerBbl) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">LBS of OBP</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.lcmReport?.lbsOfObp) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Avg LBS OBP / BBL</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.lcmReport?.avgLbsObpPerBbl) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">LBS of Fiber</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.lcmReport?.lbsOfFiber) }}</span>
          </div>
          <div class="col-12 md:col-4">
            <span class="field-label">Avg LBS Fiber / BBL</span>
            <span class="field-value">{{ formatNumber(s.outputSnapshot.lcmReport?.avgLbsFiberPerBbl) }}</span>
          </div>
        </div>
      </p-panel>
    }

    <app-chemical-dosage-chart
      [shiftSummaryId]="shiftSummaryId"
      [parquetBlobPath]="s.outputSnapshot?.chemicalDosageParquetBlobPath">
    </app-chemical-dosage-chart>

    <p-panel header="Attachments" class="mb-3">
      @if (s.attachments?.length) {
        <div class="attachment-list">
          @for (att of s.attachments!; track att.id ?? att.fileName ?? $index) {
            <div class="attachment-card">
              <div class="attachment-card-preview">
                @if (isImageAttachment(att) && getAttachmentPreviewUrl(att)) {
                  <img
                    [src]="getAttachmentPreviewUrl(att)!"
                    [alt]="att.fileName"
                    class="attachment-card-image">
                } @else {
                  <div class="attachment-card-icon">
                    <i class="pi pi-file"></i>
                  </div>
                }
              </div>

              <div class="attachment-card-body">
                <span class="field-label">
                  <a class="cursor-pointer text-primary" (click)="downloadAttachment(att.id)">{{ att.fileName }}</a>
                  @if (att.sizeBytes) {
                    <span class="ml-2 text-color-secondary text-sm">
                      ({{ (att.sizeBytes / 1024) | number:'1.0-0' }} KB)
                    </span>
                  }
                </span>
                <span class="field-value">{{ formatNA(att.description) }}</span>
              </div>
            </div>
          }
        </div>
      } @else {
        <span class="text-color-secondary">No files attached.</span>
      }
    </p-panel>

    @if (s.outputSnapshot?.dashboardUrl) {
      <div class="flex justify-content-between mt-4">
        <p-button
          label="Open Dashboard for this Shift"
          icon="pi pi-external-link"
          class="p-button-outlined"
          (onClick)="openDashboard(s.outputSnapshot!.dashboardUrl)">
        </p-button>
      </div>
    }
  </div>
} @else {
  <div class="p-4 flex align-items-center justify-content-center" style="min-height: 200px;">
    @if (loading()) {
      <p-progressSpinner></p-progressSpinner>
    }
  </div>
}
